import * as RadixToast from '@radix-ui/react-toast';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cx } from '../cx.js';

export type VarianteToast = 'success' | 'error' | 'info';
export interface MessaggioToast {
  id: number;
  variant: VarianteToast;
  title: string;
  description?: string;
}

interface ContestoToast {
  mostra: (m: Omit<MessaggioToast, 'id'>) => void;
}
const Contesto = createContext<ContestoToast | null>(null);

const stili: Record<VarianteToast, { icona: typeof Info; colore: string }> = {
  success: { icona: CircleCheck, colore: 'text-success' },
  error: { icona: CircleAlert, colore: 'text-error' },
  info: { icona: Info, colore: 'text-accent' },
};

/** Da mettere una volta attorno all'app. Le notifiche durano 6 secondi e sono annunciate agli screen reader. */
export function ToastProvider({ children, testoChiudi = 'Chiudi' }: { children: ReactNode; testoChiudi?: string }) {
  const [messaggi, setMessaggi] = useState<MessaggioToast[]>([]);
  const mostra = useCallback((m: Omit<MessaggioToast, 'id'>) => {
    setMessaggi((prev) => [...prev.slice(-3), { ...m, id: Date.now() + Math.random() }]);
  }, []);
  const valore = useMemo(() => ({ mostra }), [mostra]);
  return (
    <Contesto.Provider value={valore}>
      <RadixToast.Provider duration={6000} swipeDirection="right">
        {children}
        {messaggi.map((m) => {
          const { icona: Icona, colore } = stili[m.variant];
          return (
            <RadixToast.Root
              key={m.id}
              onOpenChange={(open) => {
                if (!open) setMessaggi((prev) => prev.filter((x) => x.id !== m.id));
              }}
              className="bg-surface rounded-lg shadow-overlay border border-border p-4 flex gap-3 items-start w-full"
            >
              <Icona aria-hidden size={20} strokeWidth={1.75} className={cx('shrink-0 mt-0.5', colore)} />
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <RadixToast.Title className={cx('t-label', colore)}>{m.title}</RadixToast.Title>
                {m.description && <RadixToast.Description className="t-body-small text-ink">{m.description}</RadixToast.Description>}
              </div>
              <RadixToast.Close aria-label={testoChiudi} className="h-11 w-11 -m-2 shrink-0 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink">
                <X aria-hidden size={18} strokeWidth={1.75} />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 w-[calc(100%-2*var(--space-6))] max-w-[400px] outline-none" />
      </RadixToast.Provider>
    </Contesto.Provider>
  );
}

export function useToast(): ContestoToast {
  const c = useContext(Contesto);
  if (!c) throw new Error('useToast va usato dentro <ToastProvider>');
  return c;
}
